import { useMemo } from 'react'
import { useProperties } from '@/hooks/useApi'
import { getPropertyDisplayName } from '@/lib/format-utils'

type PropertyLikeArg = Parameters<typeof getPropertyDisplayName>[0]

export interface PropertyOption {
  id: string
  name: string
  archived: boolean
}

/**
 * Every property, active and archived, sorted by display name, from the
 * `GET /admin/properties?include=all` list query (cached for 30 seconds and
 * shared by every property header, so switching properties reuses it).
 */
export function usePropertyOptions() {
  const query = useProperties('all')
  const options = useMemo<PropertyOption[]>(() => {
    const rows = query.data?.properties ?? []
    return rows
      .map(row => {
        const id = row.property_id || row.id
        if (!id) return null
        return {
          id,
          name: getPropertyDisplayName(row as PropertyLikeArg, id),
          archived: row.archived === true,
        }
      })
      .filter((option): option is PropertyOption => option !== null)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
  }, [query.data])
  return { ...query, options }
}
